import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mic, MicOff, Loader2, Check, AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { formatCurrency, getIcon, catColor } from '../lib/constants';
import { parseVoice, createTransaction, isPaywallError } from '../lib/api';
import { usePaywall } from '../context/PaywallContext';
import { warnIfPossibleDuplicate } from '../lib/duplicates';

const STATUS = {
  IDLE: 'idle',
  LISTENING: 'listening',
  PROCESSING: 'processing',
  PARSED: 'parsed',
  NEEDS_CATEGORY: 'needs_category',
  ERROR: 'error'
};

// `square`: the 56px square button beside "Anotar gasto" on Inicio
// (redesign.md, Fase 4); `compact`: the small round inline one.
// Set once the iPhone mic permission was granted through getUserMedia in this
// page load (see startListening).
let micPrimed = false;
const isIOSDevice = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export default function VoiceInput({ categories, onSuccess, containerTestId = 'voice-input-container', compact = false, square = false }) {
  const { openPaywall } = usePaywall();
  const [status, setStatus] = useState(STATUS.IDLE);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [matchedCategory, setMatchedCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const recognitionRef = useRef(null);
  const pulseRef = useRef(null);
  // iPhone workarounds (see startListening): latest interim text, whether a
  // final result arrived, and the no-audio watchdog / single retry.
  const interimRef = useRef('');
  const gotResultRef = useRef(false);
  const gotFinalRef = useRef(false);
  const watchdogRef = useRef(null);
  const retryingRef = useRef(false);

  const isSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  useEffect(() => {
    return () => {
      clearTimeout(watchdogRef.current);
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // iOS Safari: the first recognition session after the mic permission prompt
  // often gets no audio (it listens forever and returns nothing), and it may
  // never mark a result as final. So on iPhone the mic permission is asked
  // first through getUserMedia, a session with no results after a few seconds
  // is restarted once, and on stop the last interim text is used.
  const startListening = async (isRetry = false) => {
    if (!isSupported) {
      toast.error('Tu navegador no soporta reconocimiento de voz');
      return;
    }
    if (!isRetry && isIOSDevice() && !micPrimed && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop());
        micPrimed = true;
      } catch (_) {
        toast.error('Permiso de micrófono denegado. Actívalo en Ajustes → Safari → Micrófono.');
        return;
      }
    }
    retryingRef.current = false;
    interimRef.current = '';
    gotResultRef.current = false;
    gotFinalRef.current = false;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-ES';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      clearTimeout(watchdogRef.current);
      if (!isRetry) {
        watchdogRef.current = setTimeout(() => {
          if (gotResultRef.current || recognitionRef.current !== recognition) return;
          retryingRef.current = true;
          recognition.abort();
        }, 5000);
      }
      setStatus(STATUS.LISTENING);
      setTranscript('');
      setInterimTranscript('');
      setParsedData(null);
      setMatchedCategory(null);
      setSelectedCategoryId('');
    };

    recognition.onresult = (event) => {
      let final = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      gotResultRef.current = true;
      if (final) {
        gotFinalRef.current = true;
        setTranscript(final);
      }
      interimRef.current = interim;
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      if (retryingRef.current) return; // our own abort from the watchdog
      console.error('Speech error:', event.error);
      if (event.error === 'no-speech') {
        toast.error('No se detecto voz. Intenta de nuevo.');
      } else if (event.error === 'not-allowed' && micPrimed) {
        // Safari can refuse a start that isn't directly in a tap (after the
        // permission prompt or the watchdog's restart): the mic is allowed.
        toast.message('Micrófono listo. Toca de nuevo para hablar.');
      } else if (event.error === 'not-allowed') {
        toast.error('Permiso de microfono denegado. Activa el microfono en tu navegador.');
      }
      setStatus(STATUS.IDLE);
    };

    recognition.onend = () => {
      clearTimeout(watchdogRef.current);
      if (retryingRef.current) {
        startListening(true);
        return;
      }
      // iOS may end without ever marking a result final: keep what was heard.
      if (!gotFinalRef.current && interimRef.current.trim()) {
        setTranscript(interimRef.current);
        setInterimTranscript('');
      }
      // Auto process after recognition ends
      setStatus(prev => {
        if (prev === STATUS.LISTENING) return STATUS.IDLE;
        return prev;
      });
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (_) {
      setStatus(STATUS.IDLE);
      toast.message('Toca el micrófono de nuevo para hablar.');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  // Process transcript when it changes and recognition ends
  useEffect(() => {
    if (transcript && status === STATUS.IDLE) {
      processVoiceText(transcript);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, status]);

  const processVoiceText = async (text) => {
    if (!text.trim()) return;
    setStatus(STATUS.PROCESSING);

    try {
      const result = await parseVoice(text);
      const { parsed, category_matched, needs_category } = result;
      setParsedData(parsed);
      setMatchedCategory(category_matched);

      if (needs_category || !category_matched) {
        setStatus(STATUS.NEEDS_CATEGORY);
        setShowDialog(true);
      } else {
        setStatus(STATUS.PARSED);
        // Auto-create the transaction
        await createTransactionFromVoice(parsed, category_matched.id);
      }
    } catch (e) {
      console.error('Voice parse error:', e);
      if (isPaywallError(e)) {
        openPaywall(e.response.data.detail);
      } else {
        toast.error('Error al procesar la voz');
      }
      setStatus(STATUS.ERROR);
      setTimeout(() => setStatus(STATUS.IDLE), 2000);
    }
  };

  const createTransactionFromVoice = async (parsed, categoryId) => {
    try {
      const txnData = {
        amount: parsed.amount || 1,
        type: parsed.type || 'expense',
        category_id: categoryId,
        description: parsed.description || transcript,
        date: new Date().toISOString(),
        source: 'voice',
      };

      const created = await createTransaction(txnData);
      const isIncome = txnData.type === 'income';
      if (!warnIfPossibleDuplicate(created, onSuccess)) {
        toast.success(
          `${isIncome ? 'Ingreso' : 'Gasto'} de ${formatCurrency(txnData.amount)} registrado`,
          { duration: 4000 }
        );
      }
      setStatus(STATUS.IDLE);
      setTranscript('');
      setShowDialog(false);
      onSuccess();
    } catch (e) {
      console.error('Transaction create error:', e);
      toast.error('Error al crear la transaccion');
      setStatus(STATUS.ERROR);
      setTimeout(() => setStatus(STATUS.IDLE), 2000);
    }
  };

  const handleCategorySelect = async () => {
    if (!selectedCategoryId || !parsedData) return;
    await createTransactionFromVoice(parsedData, selectedCategoryId);
  };

  const handleCancel = () => {
    setShowDialog(false);
    setStatus(STATUS.IDLE);
    setTranscript('');
    setParsedData(null);
  };

  const isListening = status === STATUS.LISTENING;
  const isProcessing = status === STATUS.PROCESSING;

  // Filter categories by parsed type
  const filteredCategories = categories.filter(c => {
    if (!parsedData) return true;
    if (parsedData.type === 'income') return c.type === 'income';
    return c.type === 'expense' || c.type === 'both';
  });

  if (!isSupported) return null;

  return (
    <>
      {/* Voice Button */}
      <div className={`relative flex flex-col items-center gap-0.5 ${square ? "shrink-0" : ""}`} data-testid={containerTestId}>
        <Button
          data-testid="voice-input-btn"
          onClick={isListening ? stopListening : () => startListening()}
          disabled={isProcessing}
          // `compact` = the Dashboard's inline action row: same height as
          // the Agregar button beside it, no glow. The nav/sidebar keep the
          // larger glowing version as the app's floating primary action.
          className={`relative ${square ? 'rounded-2xl' : 'rounded-full'} p-0 transition-all duration-300 ${square ? 'w-14 h-14 shrink-0 active:scale-[0.95]' : compact ? 'w-11 h-11 active:scale-[0.95]' : 'w-14 h-14'} ${isListening
            ? `bg-rose-500 hover:bg-rose-600 ${compact ? '' : 'shadow-[0_0_35px_rgba(244,63,94,0.6)]'}`
            : isProcessing
              ? 'bg-slate-700 cursor-wait'
              : square
                ? 'bg-brand-soft hover:bg-brand-soft/80 text-brand'
              : compact
                ? 'bg-white/[0.06] hover:bg-white/10 text-violet-300'
                : 'bg-violet-600 hover:bg-violet-500 shadow-[0_0_25px_rgba(139,92,246,0.45)] hover:shadow-[0_0_40px_rgba(139,92,246,0.65)]'
            }`}
          title={isListening ? 'Detener' : 'Hablar para agregar transaccion'}
        >
          {isProcessing ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : isListening ? (
            <MicOff className="w-5 h-5 text-on-brand" />
          ) : (
            <Mic className={`w-5 h-5 ${square ? 'text-brand' : compact ? 'text-violet-300' : 'text-on-brand'}`} />
          )}
          {/* Pulse animation when listening */}
          {isListening && (
            <>
              <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-30" />
              <span className="absolute -inset-1 rounded-full border-2 border-rose-400/40 animate-pulse" />
            </>
          )}
        </Button>


        {/* Full-screen listening overlay — replaces the old small w-64
            floating popup. That was easy to miss entirely; while actively
            speaking, this moment should command the whole screen the way
            Siri/voice-assistant UIs do, not sit in a corner tooltip. */}
        {(isListening || isProcessing) && createPortal(
          <div
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-8 bg-surface px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
            data-testid="voice-transcript-popup"
          >
            <div className="relative flex items-center justify-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center transition-colors duration-300 ${
                isListening ? 'bg-rose-500' : 'bg-violet-600'
              }`}>
                {isProcessing ? (
                  <Loader2 className="w-10 h-10 text-on-brand animate-spin" />
                ) : (
                  <Mic className="w-10 h-10 text-on-brand" />
                )}
              </div>
              {isListening && (
                <>
                  <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-30" />
                  <span className="absolute -inset-3 rounded-full border-2 border-rose-400/30 animate-pulse" />
                </>
              )}
            </div>

            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              {isListening ? 'Escuchando...' : 'Procesando...'}
            </p>

            <p className="text-2xl sm:text-4xl font-semibold text-white text-center max-w-lg leading-snug">
              {transcript}
              {interimTranscript && (
                <span className="text-slate-400">{transcript ? ' ' : ''}{interimTranscript}</span>
              )}
              {!transcript && !interimTranscript && (
                <span className="text-slate-500 font-normal">Di algo como "gasté 50 en comida"</span>
              )}
            </p>

            {isListening && (
              <button
                type="button"
                data-testid="voice-transcript-stop-btn"
                onClick={stopListening}
                className="mt-4 h-12 px-6 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold transition-colors active:scale-[0.97]"
              >
                Detener
              </button>
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Category Selection Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 rounded-2xl max-w-md" data-testid="voice-category-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Selecciona categoria</DialogTitle>
            <DialogDescription className="text-slate-400">
              No pudimos identificar la categoria. Selecciona una para completar la transaccion.
            </DialogDescription>
          </DialogHeader>

          {parsedData && (
            <div className="space-y-4 mt-2">
              {/* Parsed summary */}
              <div className="bg-slate-800/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                  <Mic className="w-3.5 h-3.5" />
                  <span>Interpretado de tu voz:</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Texto</span>
                  <span className="text-slate-200 text-right max-w-[200px] truncate">"{transcript}"</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Monto</span>
                  <span className={`font-semibold ${parsedData.type === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatCurrency(parsedData.amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Tipo</span>
                  <span className="text-slate-200">{parsedData.type === 'income' ? 'Ingreso' : 'Gasto'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Descripcion</span>
                  <span className="text-slate-200 text-right max-w-[200px] truncate">{parsedData.description}</span>
                </div>
              </div>

              {/* Category selector */}
              <div className="space-y-2">
                <Label className="text-slate-300">Categoria</Label>
                <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white" data-testid="voice-category-select">
                    <SelectValue placeholder="Selecciona una categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {filteredCategories.map(cat => {
                      const Icon = getIcon(cat.icon);
                      return (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-3.5 h-3.5" style={{ color: catColor(cat.color) }} />
                            {cat.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                    {filteredCategories.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">No hay categorias de este tipo</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="voice-cancel-btn"
                  variant="ghost"
                  onClick={handleCancel}
                  className="flex-1 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl h-11"
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="voice-confirm-btn"
                  onClick={handleCategorySelect}
                  disabled={!selectedCategoryId}
                  className={`flex-1 rounded-xl h-11 ${parsedData.type === 'income'
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-rose-500 hover:bg-rose-600'
                    } text-on-brand disabled:opacity-40`}
                >
                  <Check className="w-4 h-4 mr-1.5" /> Confirmar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
